import { useState, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Plus } from "lucide-react";
import ConfTableRow from "@components/feature/roster_configure/ConfTableRow";
import ConfTableAddRow from "@components/feature/roster_configure/ConfTableAddRow";
import LoadingSpiner from "@components/ui/LoadingSpiner";
import { ShiftCodeDeleteDialog } from "@components/feature/roster_configure/ShiftCodeDeleteDialog";
import { SearchInput } from "@/components/common/SearchInput";

import type * as type from "@vTypes";
import * as req from "@script/req";
import { toast } from "@/components/toast/CustomToast";
import {
  useShiftsQuery,
  type ShiftReturnType,
} from "@/hooks/roster-craete/useShiftsQuery";
import { useShiftManageQuery } from "@/hooks/roster-craete/useShiftManageQuery";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { getDefaultShiftsSortedBySequence } from "@/utils/shift-util";
import { Button } from "@/components/ui/button";
import { canUseOffSwapTarget } from "./shift-code-add-dialog-utils";

interface ConfTab0Props {
  isAdmin: boolean;
  selectedGroupId: string | null;
}

const SHIFT_SEARCH_FIELD_LABELS = {
  name: "이름",
  memo: "메모",
} as const;

const SHIFT_SEARCH_PLACEHOLDERS = {
  name: "이름으로 검색",
  memo: "메모로 검색",
} as const;

type ShiftSearchField = keyof typeof SHIFT_SEARCH_FIELD_LABELS;

type SubmittedShiftSearch = {
  readonly field: ShiftSearchField;
  readonly term: string;
};

function isShiftSearchField(value: string): value is ShiftSearchField {
  return value === "name" || value === "memo";
}

export interface LocalShiftType {
  id: number;
  name: string;
  description?: string | null;
  shift_id: string;
  type: string;
  sequence: number;
  color: string;
  allday: number;
  auto_schedule: number;
  shift_gb: string | null;
  start_time: string | null;
  end_time: string | null;
  duration: number | null;
  default_shift: string | null;
  show_in_preference?: boolean;
  off_swap_target?: boolean;
}

function toUpdateShift(row: ShiftReturnType[number]): type.UpdateShift {
  return {
    ...row,
    allday: Number(row.allday),
    auto_schedule: Number(row.auto_schedule),
  };
}

function getShiftSearchValue(shift: type.UpdateShift, field: ShiftSearchField) {
  if (field === "name") return shift.name;
  return shift.description ?? "";
}

function isShiftMatchedBySearch(
  shift: type.UpdateShift,
  field: ShiftSearchField,
  normalizedSearchTerm: string
) {
  if (!normalizedSearchTerm) return true;

  return getShiftSearchValue(shift, field)
    .toLowerCase()
    .includes(normalizedSearchTerm);
}

export default function ConfTab1({ isAdmin, selectedGroupId }: ConfTab0Props) {
  const queryClient = useQueryClient();
  const [addNewShifts, setAddNewShifts] = useState<boolean>(false);
  const [shiftSearchField, setShiftSearchField] =
    useState<ShiftSearchField>("name");
  const [shiftSearchInput, setShiftSearchInput] = useState("");
  const [submittedShiftSearch, setSubmittedShiftSearch] =
    useState<SubmittedShiftSearch>({
      field: "name",
      term: "",
    });

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [pendingShiftRowCounts, setPendingShiftRowCounts] = useState<
    Map<string, number>
  >(() => new Map());

  // OFF 대체 코드 변경 중에만 임시로 표시할 낙관적 목록
  const [optimisticShifts, setOptimisticShifts] =
    useState<ShiftReturnType | null>(null);

  const targetGroupId = isAdmin ? (selectedGroupId ?? "") : "";

  const markShiftRowPending = (rowKey: string) => {
    setPendingShiftRowCounts((prev) => {
      const next = new Map(prev);
      next.set(rowKey, (next.get(rowKey) ?? 0) + 1);
      return next;
    });
  };

  const unmarkShiftRowPending = (rowKey: string) => {
    setPendingShiftRowCounts((prev) => {
      const next = new Map(prev);
      const nextCount = (next.get(rowKey) ?? 1) - 1;
      if (nextCount <= 0) {
        next.delete(rowKey);
      } else {
        next.set(rowKey, nextCount);
      }
      return next;
    });
  };

  const { data: shiftsData, isLoading: isShiftsLoading } =
    useShiftsQuery(targetGroupId);

  const { data: shiftManageData, isLoading: isManageLoading } =
    useShiftManageQuery(targetGroupId);

  const displayShifts = useMemo(
    () => optimisticShifts ?? shiftsData ?? [],
    [optimisticShifts, shiftsData]
  );

  const displayUpdateShifts = useMemo(
    () => displayShifts.map(toUpdateShift),
    [displayShifts]
  );

  const shiftIdFlatMap = useMemo(
    () => displayShifts.map((s) => s.shift_id),
    [displayShifts]
  );

  // Mutations
  const addMutation = useMutation({
    mutationFn: (data: type.AddShift) => req.addShift(data, targetGroupId),
    onSuccess: () => {
      toast.success("추가되었습니다");
      setAddNewShifts(false);
      setOptimisticShifts(null);
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      queryClient.invalidateQueries({
        queryKey: ["shiftManager"],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: type.UpdateShift) =>
      req.updateShift(data, targetGroupId),
    onSuccess: async () => {
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["SHIFTS"],
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.shifts.shiftManager(targetGroupId),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.config.configVersions(targetGroupId ?? ""),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.config.rosterConfigVersions(targetGroupId ?? ""),
        }),
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.config.configData(targetGroupId ?? ""),
        }),
      ]);
      toast.success("수정되었습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (shiftId: string) => req.deleteShift(shiftId, targetGroupId),
    onSuccess: () => {
      toast.success("삭제되었습니다.");
      // ["SHIFTS"]로 시작하는 모든 쿼리 무효화 (null, "", groupId 모두 커버)
      queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      setIsConfirmModalOpen(false);
      setDeleteTargetId(null);
    },
    onError: () => {
      setOptimisticShifts(null);
      setIsConfirmModalOpen(false);
      setDeleteTargetId(null);
    },
  });

  const saveManageMutation = useMutation({
    mutationFn: (payload: type.ShiftManageObject[]) =>
      req.saveShiftManage(payload, targetGroupId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["shiftManager", targetGroupId],
      });
    },
  });

  // defaultCode: 메인 시프트 (default_shift 있음, 서브코드 제외) - sequence 순
  const offSwapTargetMutation = useMutation({
    mutationFn: async ({
      target,
      previousTargets,
    }: {
      target: type.UpdateShift;
      previousTargets: type.UpdateShift[];
    }) => {
      if (previousTargets.length > 0) {
        await Promise.all(
          previousTargets.map((previous) =>
            req.updateShift(
              {
                ...previous,
                off_swap_target: false,
              },
              targetGroupId
            )
          )
        );
      }

      return req.updateShift(
        {
          ...target,
          off_swap_target: Boolean(target.off_swap_target),
        },
        targetGroupId
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["SHIFTS"],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configVersions(targetGroupId ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.rosterConfigVersions(targetGroupId ?? ""),
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.config.configData(targetGroupId ?? ""),
      });
      setOptimisticShifts(null);
      toast.success("OFF 대체 코드가 변경되었습니다.");
    },
    onError: () => {
      setOptimisticShifts(null);
    },
  });

  const offSwapOffShiftGb = useMemo(
    () => displayShifts.find((shift) => shift.default_shift === "O")?.shift_gb,
    [displayShifts]
  );

  const isOffSwapCandidate = (shift: {
    type: string;
    default_shift?: string | null;
    shift_gb?: string | null;
  }) => canUseOffSwapTarget(shift, offSwapOffShiftGb);

  const handleUpdateShift = (targetShift: type.UpdateShift, rowKey: string) => {
    const nextShift = isOffSwapCandidate(targetShift)
      ? targetShift
      : {
          ...targetShift,
          off_swap_target: false,
        };
    const originalShift = displayUpdateShifts.find(
      (shift) => shift.id === nextShift.id
    );
    const offSwapTargetChanged =
      Boolean(originalShift?.off_swap_target) !==
      Boolean(nextShift.off_swap_target);
    const previousTargets = nextShift.off_swap_target
      ? displayUpdateShifts.filter(
          (shift) =>
            shift.off_swap_target &&
            shift.id !== nextShift.id &&
            isOffSwapCandidate(shift)
        )
      : [];

    if (nextShift.off_swap_target || offSwapTargetChanged) {
      setOptimisticShifts(
        displayShifts.map((shift) => ({
          ...shift,
          off_swap_target:
            shift.id === nextShift.id
              ? Boolean(nextShift.off_swap_target)
              : nextShift.off_swap_target && isOffSwapCandidate(shift)
                ? false
                : shift.off_swap_target,
        }))
      );
      markShiftRowPending(rowKey);
      offSwapTargetMutation.mutate(
        {
          target: nextShift,
          previousTargets,
        },
        {
          onSettled: () => unmarkShiftRowPending(rowKey),
        }
      );
      return;
    }

    markShiftRowPending(rowKey);
    updateMutation.mutate(nextShift, {
      onSettled: () => unmarkShiftRowPending(rowKey),
    });
  };

  const handleSaveShiftManage = (
    payload: type.ShiftManageObject[],
    rowKey: string
  ) => {
    markShiftRowPending(rowKey);
    saveManageMutation.mutate(payload, {
      onSettled: () => unmarkShiftRowPending(rowKey),
    });
  };

  const defaultCode = useMemo(
    () => getDefaultShiftsSortedBySequence(displayUpdateShifts),
    [displayUpdateShifts]
  );

  // defaultShiftManage: 나머지 시프트 (원본 순서)
  const defaultShiftManage = useMemo(() => {
    const defaultIds = new Set(defaultCode.map((s) => s.shift_id));
    return displayUpdateShifts.filter((s) => !defaultIds.has(s.shift_id));
  }, [displayUpdateShifts, defaultCode]);

  const normalizedShiftSearchTerm = submittedShiftSearch.term
    .trim()
    .toLowerCase();
  const filteredDefaultCode = useMemo(
    () =>
      defaultCode.filter((shift) =>
        isShiftMatchedBySearch(
          shift,
          submittedShiftSearch.field,
          normalizedShiftSearchTerm
        )
      ),
    [defaultCode, normalizedShiftSearchTerm, submittedShiftSearch.field]
  );
  const filteredDefaultShiftManage = useMemo(
    () =>
      defaultShiftManage.filter((shift) =>
        isShiftMatchedBySearch(
          shift,
          submittedShiftSearch.field,
          normalizedShiftSearchTerm
        )
      ),
    [defaultShiftManage, normalizedShiftSearchTerm, submittedShiftSearch.field]
  );
  const hasFilteredShifts =
    filteredDefaultCode.length > 0 || filteredDefaultShiftManage.length > 0;

  const handleDelete = (shiftId: string) => {
    setDeleteTargetId(shiftId);
    setIsConfirmModalOpen(true);
  };

  const submitShiftSearch = () => {
    setSubmittedShiftSearch({
      field: shiftSearchField,
      term: shiftSearchInput.trim(),
    });
  };

  const handleConfirmDelete = () => {
    if (deleteTargetId) {
      deleteMutation.mutate(deleteTargetId);
    }
  };

  if (isShiftsLoading || isManageLoading)
    return (
      <div className="flex min-h-[200px] w-full items-center justify-center py-12">
        <LoadingSpiner msg="근무코드 설정을 불러오는 중..." />
      </div>
    );

  return (
    <div className="mx-auto flex w-full flex-col gap-3 self-center max-w-[1400px]">
      <div className="px-[18px]">
        <div className="min-w-0">
          <h1 className="font-pretendard text-[24px] font-bold tracking-normal text-[#0E59C6]">
            근무코드 설정
          </h1>
          <p className="mt-1 font-pretendard text-sm leading-5 text-muted-foreground">
            근무표에 사용할 코드와 시간, 원티드 반영 여부를 관리합니다.
          </p>
        </div>
      </div>
      <div className="w-full min-w-0 max-w-full px-2 pb-2">
        <div className="conf_shiftCode__toolbar">
          <SearchInput
            aria-label={`근무코드 ${SHIFT_SEARCH_FIELD_LABELS[shiftSearchField]} 검색`}
            name="shift-search"
            autoComplete="off"
            placeholder={SHIFT_SEARCH_PLACEHOLDERS[shiftSearchField]}
            value={shiftSearchInput}
            onChange={(event) => setShiftSearchInput(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.nativeEvent.isComposing) {
                submitShiftSearch();
              }
            }}
            onSearch={submitShiftSearch}
            searchButtonLabel={`근무코드 ${SHIFT_SEARCH_FIELD_LABELS[shiftSearchField]} 검색 실행`}
            leading={
              <label className="relative flex h-full items-center">
                <span className="sr-only">검색 기준</span>
                <select
                  aria-label="검색 기준"
                  value={shiftSearchField}
                  onChange={(event) => {
                    const nextField = event.currentTarget.value;
                    if (isShiftSearchField(nextField)) {
                      setShiftSearchField(nextField);
                    }
                  }}
                  className="h-full appearance-none bg-transparent py-0 pl-4 pr-9 font-pretendard text-sm font-semibold text-[#333333] outline-none"
                >
                  <option value="name">이름</option>
                  <option value="memo">메모</option>
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 size-4 text-[#939399]"
                  aria-hidden="true"
                />
              </label>
            }
            containerClassName="h-11 w-full max-w-[420px] border-[#D6D6DE]"
            inputClassName="h-11 text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit shrink-0 border-sig text-sig hover:bg-sig/10"
            disabled={addNewShifts}
            onClick={() => setAddNewShifts(true)}
          >
            <Plus data-icon="inline-start" />
            코드 추가
          </Button>
        </div>
        <div className="conf_shiftCode__table">
          <div className="conf_shiftCode__gridTemplate conf_shiftCode__thead">
            <div className="font-pretendard">No.</div>
            <div className="font-pretendard">근무코드</div>
            <div className="font-pretendard">이름</div>
            <div className="font-pretendard th_type">구분</div>
            <div className="font-pretendard">근무시간</div>
            <div className="font-pretendard th_type">타입</div>
            <div className="font-pretendard">OFF 대체</div>
            <div className="font-pretendard">원티드 반영</div>
            <div className="font-pretendard">메모</div>
            <div className="font-pretendard">관리</div>
          </div>
          <div className="conf_shiftCode__tbody">
            {/* defaultCode: 메인 시프트 (sequence 순, 정렬 불가) — 근무코드·이름 등 수정, 구분/타입은 고정 */}
            {filteredDefaultCode.map((arr, index) => (
              <ConfTableRow
                key={arr.shift_id}
                rowNumber={index + 1}
                data={arr}
                updateShift={(value) => handleUpdateShift(value, arr.shift_id)}
                deleteShift={(shift_id) => handleDelete(shift_id)}
                currentShiftManage={shiftManageData || []}
                saveShiftManage={(payload) =>
                  handleSaveShiftManage(payload, arr.shift_id)
                }
                shiftIdFlatMap={shiftIdFlatMap}
                offSwapOffShiftGb={offSwapOffShiftGb}
                isPending={(pendingShiftRowCounts.get(arr.shift_id) ?? 0) > 0}
                searchTerm={submittedShiftSearch.term}
                searchField={submittedShiftSearch.field}
              />
            ))}
            {/* defaultShiftManage: 나머지 시프트 */}
            {filteredDefaultShiftManage.map((arr, index) => (
              <ConfTableRow
                key={arr.shift_id}
                rowNumber={filteredDefaultCode.length + index + 1}
                data={arr}
                updateShift={(value) => handleUpdateShift(value, arr.shift_id)}
                deleteShift={(shift_id) => handleDelete(shift_id)}
                currentShiftManage={shiftManageData || []}
                saveShiftManage={(payload) =>
                  handleSaveShiftManage(payload, arr.shift_id)
                }
                shiftIdFlatMap={shiftIdFlatMap}
                offSwapOffShiftGb={offSwapOffShiftGb}
                isPending={(pendingShiftRowCounts.get(arr.shift_id) ?? 0) > 0}
                searchTerm={submittedShiftSearch.term}
                searchField={submittedShiftSearch.field}
              />
            ))}
            {!hasFilteredShifts && (
              <div className="conf_shiftCode__row empty">
                {SHIFT_SEARCH_FIELD_LABELS[submittedShiftSearch.field]} 검색
                결과가 없습니다.
              </div>
            )}
          </div>
        </div>
      </div>

      {addNewShifts && (
        <ConfTableAddRow
          setAddNewShifts={setAddNewShifts}
          addShift={(data) => addMutation.mutate(data)}
          currentShiftManage={shiftManageData || []}
          saveShiftManage={(payload) =>
            handleSaveShiftManage(payload, "__new_shift__")
          }
          shiftIdFlatMap={shiftIdFlatMap}
          offSwapOffShiftGb={offSwapOffShiftGb}
          isPending={addMutation.isPending}
        />
      )}

      <ShiftCodeDeleteDialog
        open={isConfirmModalOpen}
        isPending={deleteMutation.isPending}
        onOpenChange={(open) => {
          setIsConfirmModalOpen(open);
          if (open) return;
          setIsConfirmModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
